import { customApiCall } from "utils/axios";

import {
  collectInputFiles,
  immediateInputChildFolders,
  type InputDirectoryListing,
  isIgnoredInputFolder,
  normalizeRelativeFolder,
  parseInputFolderEntries,
} from "../lib/collectInputFiles";
import { collectResultFolders } from "../lib/collectResultFolders";
import { RESULT_LOG_FOLDER, type TransientInputFile, type TransientResultFolder } from "../lib/formatJobResults";
import { isRecord } from "../lib/parseApiId";
import {
  isJsonFileName,
  isLogFileName,
  isXlsxFileName,
  lastPathSegment,
  parseStoredDirectory,
} from "../lib/storedDirectory";
import { RESULT_KIND, type ResultKind } from "../model/constants";
import { transientJobUrls, transientLogFileName } from "./urls";

const isNotFoundError = (error: unknown) => {
  if (!isRecord(error)) return false;
  const response = error.response;
  if (!isRecord(response)) return false;
  return response.status === 404;
};

const listDirectory = async (url: string) => {
  try {
    return parseStoredDirectory(await customApiCall.get({ url }));
  } catch (error) {
    if (isNotFoundError(error)) return parseStoredDirectory(null);
    throw error;
  }
};

const isResultFileName = (kind: ResultKind, name: string) =>
  kind === RESULT_KIND.hourly ? isXlsxFileName(name) : isJsonFileName(name);

const listFolderResultFiles = async (
  projectId: string,
  configId: number,
  kind: ResultKind,
  folderName: string
) => {
  const listing = await listDirectory(transientJobUrls.resultsFolder(projectId, configId, kind, folderName));
  return listing.files.flatMap((file) => {
    const name = lastPathSegment(file.name);
    return isResultFileName(kind, name) ? [{ name, path: file.path, url: file.url }] : [];
  });
};

export const fetchLogResultFolder = async (projectId: string, configId?: number): Promise<TransientResultFolder[]> => {
  if (configId == null) return [];
  const logFiles = (await listDirectory(transientJobUrls.logDirectory(projectId, configId))).files.flatMap((file) => {
    const name = lastPathSegment(file.name) || lastPathSegment(file.path ?? "");
    return isLogFileName(name) ? [{ name, path: file.path, url: file.url }] : [];
  });
  const expectedName = transientLogFileName(configId).toLowerCase();
  const matched = logFiles.filter((file) => file.name.toLowerCase() === expectedName);
  const files = (matched.length > 0 ? matched : logFiles).sort((a, b) => a.name.localeCompare(b.name));
  if (files.length === 0) return [];
  return [{ files, folderName: RESULT_LOG_FOLDER }];
};

const joinRelativePath = (...parts: string[]) => normalizeRelativeFolder(parts.filter(Boolean).join("/"));

const pendingInputLists = new Map<string, Promise<InputDirectoryListing>>();
const pendingInputTrees = new Map<string, Promise<TransientInputFile[]>>();

const listInputDirectory = (projectId: string, configId: number, relativeFolder = "") => {
  const folder = normalizeRelativeFolder(relativeFolder);
  const url = transientJobUrls.inputDirectory(projectId, configId, folder);
  const cacheKey = url.replace(/\/+$/, "");
  const pending = pendingInputLists.get(cacheKey);
  if (pending) return pending;
  const request = customApiCall
    .get({ url: cacheKey })
    .then((response) => ({
      files: parseStoredDirectory(response).files,
      folders: parseInputFolderEntries(response),
    }))
    .catch((error) => {
      if (isNotFoundError(error)) return { files: [], folders: [] };
      throw error;
    })
    .finally(() => pendingInputLists.delete(cacheKey));
  pendingInputLists.set(cacheKey, request);
  return request;
};

const listInputTree = async (
  projectId: string,
  configId: number,
  relativeFolder = "",
  visited = new Set<string>(),
  depth = 0
): Promise<InputDirectoryListing[]> => {
  const folder = normalizeRelativeFolder(relativeFolder);
  const key = folder.toLowerCase();
  if (depth > 8 || visited.has(key) || isIgnoredInputFolder(folder)) return [];
  visited.add(key);
  const listing = await listInputDirectory(projectId, configId, folder);
  const nested = await Promise.all(
    immediateInputChildFolders(listing.folders, folder).map((child) =>
      listInputTree(projectId, configId, joinRelativePath(folder, child), visited, depth + 1)
    )
  );
  return [listing, ...nested.flat()];
};

export const fetchInputFiles = async (projectId: string, configId?: number): Promise<TransientInputFile[]> => {
  if (configId == null) return [];
  const treeKey = `${projectId}:${configId}`;
  const pending = pendingInputTrees.get(treeKey);
  if (pending) return pending;
  const request = listInputTree(projectId, configId)
    .then((listings) => collectInputFiles(listings, projectId, configId))
    .finally(() => pendingInputTrees.delete(treeKey));
  pendingInputTrees.set(treeKey, request);
  return request;
};

export const fetchResultFolders = async (
  projectId: string,
  configId: number,
  kind: ResultKind
): Promise<TransientResultFolder[]> => {
  const collected = collectResultFolders(
    await listDirectory(transientJobUrls.resultsDirectory(projectId, configId, kind)),
    (name) => isResultFileName(kind, name)
  );
  const filled = await Promise.all(
    collected.map(async (folder) => {
      if (folder.files.length > 0) return folder;
      const files = await listFolderResultFiles(projectId, configId, kind, folder.folderName);
      return {
        files: files.sort((a, b) => a.name.localeCompare(b.name)),
        folderName: folder.folderName,
      };
    })
  );
  return filled.filter((folder) => folder.files.length > 0);
};
